/**
 * 캐시된 프로젝트 상세 조회 훅
 *
 * 온라인: GraphQL 쿼리 → 결과를 IndexedDB에 캐시
 * 오프라인: IndexedDB에서 캐시된 데이터 조회
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { PROJECT_QUERY } from "@/lib/graphql/projects";
import { getCachedProject, cacheProject } from "@/lib/db/projects";
import { useNetworkStatus } from "./useNetworkStatus";
import type { CachedProject, CachedEquipmentStatus } from "@/lib/db/types";

interface UseCachedProjectResult {
  project: CachedProject | null;
  equipmentStatus: CachedEquipmentStatus | null;
  loading: boolean;
  error: Error | null;
  isFromCache: boolean;
  refetch: () => void;
  /** 캐시 데이터만 다시 로드 (오프라인에서 로컬 데이터 변경 후 사용) */
  reloadCache: () => Promise<void>;
}

export function useCachedProject(projectId: string | undefined): UseCachedProjectResult {
  const { isOffline } = useNetworkStatus();
  const [cachedData, setCachedData] = useState<CachedProject | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(true);
  // 캐시 리로드 트리거 (값이 변경되면 useEffect가 다시 실행됨)
  const [cacheReloadTrigger, setCacheReloadTrigger] = useState(0);

  // GraphQL 쿼리 (온라인 시에만 실행)
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery(PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId || isOffline,
    fetchPolicy: isOffline ? "cache-only" : "cache-and-network",
  });

  // 캐시에서 로드하는 함수
  const loadFromCache = useCallback(async () => {
    if (!projectId) {
      setCacheLoading(false);
      return;
    }

    setCacheLoading(true);
    try {
      const cached = await getCachedProject(projectId);
      if (cached) {
        setCachedData(cached);
        setIsFromCache(true);
      }
    } catch (err) {
      console.error("[useCachedProject] 캐시 로드 실패:", err);
    } finally {
      setCacheLoading(false);
    }
  }, [projectId]);

  // 오프라인 시 또는 cacheReloadTrigger 변경 시 캐시에서 로드
  useEffect(() => {
    if (isOffline || cacheReloadTrigger > 0) {
      loadFromCache();
    } else if (!isOffline && cacheReloadTrigger === 0) {
      // 온라인이고 초기 상태면 로딩 해제
      setCacheLoading(false);
    }
  }, [projectId, isOffline, cacheReloadTrigger, loadFromCache]);

  // 온라인 데이터 수신 시 캐시에 저장
  useEffect(() => {
    async function saveToCache() {
      if (!isOffline && data?.project && projectId) {
        try {
          const projectToCache: CachedProject = {
            ...data.project,
            synced: true,
            cachedAt: Date.now(),
            equipmentStatus: data.projectEquipmentStatus || undefined,
          };
          await cacheProject(projectToCache);
          setCachedData(projectToCache);
          setIsFromCache(false);
        } catch (err) {
          console.error("[useCachedProject] 캐시 저장 실패:", err);
        }
      }
    }

    saveToCache();
  }, [isOffline, data, projectId]);

  // 온라인: GraphQL refetch, 오프라인: 캐시 리로드
  const handleRefetch = useCallback(() => {
    if (!isOffline) {
      refetch();
    } else {
      // 오프라인에서는 캐시 리로드
      setCacheReloadTrigger((prev) => prev + 1);
    }
  }, [isOffline, refetch]);

  // 캐시만 리로드 (오프라인에서 로컬 데이터 변경 후 UI 갱신용)
  const reloadCache = useCallback(async () => {
    await loadFromCache();
  }, [loadFromCache]);

  // 결과 결정
  const project = isOffline
    ? cachedData
    : cachedData ||
      (data?.project ? { ...data.project, synced: true, cachedAt: Date.now() } : null);
  const equipmentStatus = isOffline
    ? cachedData?.equipmentStatus || null
    : data?.projectEquipmentStatus || cachedData?.equipmentStatus || null;

  const loading = isOffline ? cacheLoading : queryLoading;
  const error = isOffline ? null : (queryError ?? null);

  return {
    project,
    equipmentStatus,
    loading,
    error,
    isFromCache,
    refetch: handleRefetch,
    reloadCache,
  };
}
